<template>
  <div class="report-layout">
    <!-- Navbar - yalnız məlumat yükləndikdən sonra göstərilir -->
    <Navbar v-if="!loading && !error" :client-name="client ? client.ClientName : ''" @print="printReport" />

    <!-- Yüklənir -->
    <div v-if="loading" class="container">
      <div class="card-body text-center">
        <h3><i class="fa-solid fa-spinner fa-spin"></i> Yüklənir...</h3>
      </div>
    </div>

    <!-- Xəta -->
    <div v-else-if="error" class="container">
      <div class="card-body text-center">
        <h3><i class="fa-solid fa-times text-danger"></i> {{ error }}</h3>
      </div>
    </div>

    <main v-else class="report-main">
      <!-- Content -->
      <div class="report-content">
        <!-- Summary Stats -->
        <div class="grid-5">
          <div class="stat-card card-purple">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div>
                <div class="stat-label">Sifariş Sayı</div>
                <div class="stat-value">{{ sessions.length }}</div>
              </div>
              <div class="stat-icon-circle bg-purple-light">
                <i class="fas fa-file-invoice"></i>
              </div>
            </div>
          </div>
          <div class="stat-card card-violet">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div>
                <div class="stat-label">Orta Qaynaq Faizi</div>
                <div class="stat-value">{{ totalWeldPct }}%</div>
              </div>
              <div class="stat-icon-circle bg-violet-light">
                <i class="fas fa-wrench"></i>
              </div>
            </div>
          </div>
          <div class="stat-card card-green">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div>
                <div class="stat-label">Orta Malyar Faizi</div>
                <div class="stat-value">{{ totalMalyarPct }}%</div>
              </div>
              <div class="stat-icon-circle bg-green-light">
                <i class="fas fa-paintbrush"></i>
              </div>
            </div>
          </div>
          <div class="stat-card card-orange">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div>
                <div class="stat-label">Orta Quraşdırma Faizi</div>
                <div class="stat-value">{{ totalInstallPct }}%</div>
              </div>
              <div class="stat-icon-circle bg-orange-light">
                <i class="fas fa-screwdriver-wrench"></i>
              </div>
            </div>
          </div>
          <div class="stat-card card-blue">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div>
                <div class="stat-label">Ümumi Həcm (m²)</div>
                <div class="stat-value">{{ formatVolume(totalVolume) }}</div>
              </div>
              <div class="stat-icon-circle bg-blue-light">
                <i class="fas fa-boxes-stacked"></i>
              </div>
            </div>
          </div>
        </div>

        <!-- Search and Header Section -->
        <div class="section-header">
          <h6 class="fw-bold mb-0 text-dark uppercase" style="font-size: 15px; letter-spacing: 0.05em;">SİFARİŞLƏR</h6>
          <div class="d-flex gap-2 align-items-center">
            <input
              v-model="searchQuery"
              class="search-input"
              placeholder="Sifariş axtar..."
              id="search-sessions"
            />
          </div>
        </div>

        <!-- Sessions Table -->
        <div class="table-wrap">
          <table class="report-table">
            <thead>
              <tr>
                <th style="width: 50px; text-align: center;">#</th>
                <th>Sifariş</th>
                <th style="text-align: center; width: 160px;">Ümumi Həcm (m²)</th>
                <th style="text-align: center; width: 120px;">Qaynaq</th>
                <th style="text-align: center; width: 120px;">Malyar</th>
                <th style="text-align: center; width: 120px;">Quraşdırma</th>
                <th style="text-align: left; width: 350px;">Həcm Faizi</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(session, index) in filteredSessions"
                :key="session.SessionId"
                style="cursor: pointer;"
                @click="openSessionModal(session)"
              >
                <td style="text-align: center; font-weight: bold; color: #8392ab;">{{ index + 1 }}</td>
                <td style="font-weight: bold; color: #409eff;">{{ session.SessionName }}</td>
                <td style="text-align: center; font-weight: bold;">{{ formatVolume(session.TotalVolume) }}</td>
                <td style="text-align: center;">
                  <span :class="['pct-badge', session.WeldPercentage === 100 ? 'pct-done' : 'pct-grey']">
                    {{ session.WeldPercentage }}%
                  </span>
                </td>
                <td style="text-align: center;">
                  <span :class="['pct-badge', session.PaintPercentage === 100 ? 'pct-done' : 'pct-blue']">
                    {{ session.PaintPercentage }}%
                  </span>
                </td>
                <td style="text-align: center;">
                  <span :class="['pct-badge', session.InstallPercentage === 100 ? 'pct-done' : 'pct-blue']">
                    {{ session.InstallPercentage }}%
                  </span>
                </td>
                <td>
                  <div class="pct-bar-wrapper">
                    <div class="pct-bar-track">
                      <div
                        class="pct-bar-fill"
                        :class="session.ProcessedPercentage === 100 ? 'pct-bar-done' : 'pct-bar-volume'"
                        :style="{ width: session.ProcessedPercentage + '%' }"
                      ></div>
                    </div>
                    <span class="pct-bar-label">{{ session.ProcessedPercentage }}%</span>
                    <span v-if="session.ProcessedPercentage < 100" class="pct-bar-remaining">
                      {{ 100 - session.ProcessedPercentage }}% qalıb
                    </span>
                    <span v-else class="pct-bar-complete">✓ Tamamdır</span>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredSessions.length === 0">
                <td colspan="7" class="text-center py-4 text-muted">
                  <i class="fas fa-inbox me-2"></i> Heç bir sifariş tapılmadı.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Legend -->
        <div class="legend-card mt-3">
          <div class="legend-title">
            <i class="fas fa-circle-info text-primary"></i>
            Faiz Göstəriciləri Haqqında
          </div>
          <div class="legend-items">
            <div class="legend-item">
              <div class="legend-dot" style="background: #198754;"></div>
              <span class="text-secondary">80-100%: Yüksək hazırlıq</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot" style="background: #ffc107;"></div>
              <span class="text-secondary">50-79%: Orta hazırlıq</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot" style="background: #dc3545;"></div>
              <span class="text-secondary">0-49%: Aşağı hazırlıq</span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Session Detail Modal -->
    <div v-if="selectedSession" class="modal-overlay">
      <div class="modal-content-wide" @click.stop>
        <!-- Gradient Header matching web app style -->
        <div class="modal-header-gradient">
          <button class="back-btn" @click="closeModal">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div class="header-info">
            <h4 class="modal-title-text">{{ selectedSession.SessionName }}</h4>
            <p class="modal-subtitle-text">Müştəri: {{ client ? client.ClientName : '' }}</p>
          </div>
        </div>
        
        <div class="modal-body-scrollable">
          <!-- Sifariş Məlumatı Card matching Məhsul Məlumatı card layout -->
          <div class="card modal-info-card mb-4 border-0">
            <div class="card-body p-3">
              <h5 class="card-title mb-3">Sifariş Məlumatı</h5>
              <div class="row mb-2">
                <div class="col-8 col-sm-9">
                  <span class="fw-bold text-secondary small" style="font-size: 11px;">Müştəri</span>
                  <p class="mb-0 fw-bold text-dark" style="font-size: 13px;">
                    {{ client ? client.ClientName : '' }}
                  </p>
                </div>
                <div class="col-4 col-sm-3">
                  <span class="fw-bold text-secondary small" style="font-size: 11px;">Həcm</span>
                  <p class="mb-0 fw-bold text-dark font-mono" style="font-size: 13px;">
                    {{ formatVolume(selectedSession.TotalVolume) }} m²
                  </p>
                </div>
              </div>
              <div class="row g-2 mt-2 pt-2 border-top">
                <div class="col-4">
                  <span class="fw-bold text-secondary small" style="font-size: 11px;">Qaynaq</span>
                  <p class="mb-0 fw-bold" :class="getPctClass(selectedSession.WeldPercentage)" style="font-size: 13px;">
                    {{ selectedSession.WeldPercentage }}%
                  </p>
                </div>
                <div class="col-4">
                  <span class="fw-bold text-secondary small" style="font-size: 11px;">Malyar</span>
                  <p class="mb-0 fw-bold" :class="getPctClass(selectedSession.PaintPercentage)" style="font-size: 13px;">
                    {{ selectedSession.PaintPercentage }}%
                  </p>
                </div>
                <div class="col-4">
                  <span class="fw-bold text-secondary small" style="font-size: 11px;">Quraşdırma</span>
                  <p class="mb-0 fw-bold" :class="getPctClass(selectedSession.InstallPercentage)" style="font-size: 13px;">
                    {{ selectedSession.InstallPercentage }}%
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div v-if="modalLoading" class="text-center py-5">
            <i class="fas fa-spinner fa-spin text-primary fa-2x me-2"></i>
            <p class="text-secondary mt-2">Məlumatlar yüklənir...</p>
          </div>
          
          <div v-else>
            <!-- Locations Section -->
            <h6 class="fw-bold text-secondary mb-3 small text-uppercase" style="letter-spacing: 0.05em; font-size: 12px; display: flex; align-items: center; gap: 6px;">
              <i class="fas fa-location-dot text-primary"></i> Məkanlar
            </h6>
            <div v-if="sessionLocations.length > 0" class="table-wrap mb-4">
              <table class="report-table">
                <thead>
                  <tr>
                    <th style="width: 50px; text-align: center;">#</th>
                    <th>Məkan</th>
                    <th style="text-align: center; width: 160px;">Ümumi Həcm (m²)</th>
                    <th style="text-align: center; width: 120px;">Qaynaq</th>
                    <th style="text-align: center; width: 120px;">Malyar</th>
                    <th style="text-align: center; width: 120px;">Quraşdırma</th>
                    <th style="text-align: left; width: 350px;">Həcm Faizi</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(loc, idx) in sessionLocations" :key="loc.Loc">
                    <td style="text-align: center; font-weight: bold; color: #8392ab;">{{ idx + 1 }}</td>
                    <td style="font-weight: bold; color: #344767;">{{ loc.Loc }}</td>
                    <td style="text-align: center; font-weight: bold;">{{ formatVolume(loc.TotalVolume) }}</td>
                    <td style="text-align: center;">
                      <span :class="['pct-badge', loc.WeldPercentage === 100 ? 'pct-done' : 'pct-grey']">
                        {{ loc.WeldPercentage }}%
                      </span>
                    </td>
                    <td style="text-align: center;">
                      <span :class="['pct-badge', loc.PaintPercentage === 100 ? 'pct-done' : 'pct-blue']">
                        {{ loc.PaintPercentage }}%
                      </span>
                    </td>
                    <td style="text-align: center;">
                      <span :class="['pct-badge', loc.InstallPercentage === 100 ? 'pct-done' : 'pct-blue']">
                        {{ loc.InstallPercentage }}%
                      </span>
                    </td>
                    <td>
                      <div class="pct-bar-wrapper">
                        <div class="pct-bar-track">
                          <div
                            class="pct-bar-fill"
                            :class="loc.ProcessedPercentage === 100 ? 'pct-bar-done' : 'pct-bar-volume'"
                            :style="{ width: loc.ProcessedPercentage + '%' }"
                          ></div>
                        </div>
                        <span class="pct-bar-label">{{ loc.ProcessedPercentage }}%</span>
                        <span v-if="loc.ProcessedPercentage < 100" class="pct-bar-remaining">
                          {{ 100 - loc.ProcessedPercentage }}% qalıb
                        </span>
                        <span v-else class="pct-bar-complete">✓ Tamamdır</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center py-4 text-muted border rounded bg-white mb-4">
              <i class="fas fa-inbox me-2"></i> Məkan məlumatı yoxdur
            </div>
            
            <!-- Products Section -->
            <h6 class="fw-bold text-secondary mb-3 small text-uppercase" style="letter-spacing: 0.05em; font-size: 12px; display: flex; align-items: center; gap: 6px;">
              <i class="fas fa-cubes text-primary"></i> Məhsul Növləri
            </h6>
            <div v-if="sessionProducts.length > 0" class="table-wrap mb-4">
              <table class="report-table">
                <thead>
                  <tr>
                    <th style="width: 50px; text-align: center;">#</th>
                    <th>Məhsul Növü</th>
                    <th style="text-align: center; width: 160px;">Ümumi Həcm (m²)</th>
                    <th style="text-align: center; width: 120px;">Qaynaq</th>
                    <th style="text-align: center; width: 120px;">Malyar</th>
                    <th style="text-align: center; width: 120px;">Quraşdırma</th>
                    <th style="text-align: left; width: 350px;">Həcm Faizi</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(prod, idx) in sessionProducts" :key="prod.ProductId">
                    <td style="text-align: center; font-weight: bold; color: #8392ab;">{{ idx + 1 }}</td>
                    <td style="font-weight: bold; color: #344767;" :title="prod.ProductName">{{ prod.ProductName }}</td>
                    <td style="text-align: center; font-weight: bold;">{{ formatVolume(prod.Volume) }}</td>
                    <td style="text-align: center;">
                      <span :class="['pct-badge', prod.WeldPercentage === 100 ? 'pct-done' : 'pct-grey']">
                        {{ prod.WeldPercentage }}%
                      </span>
                    </td>
                    <td style="text-align: center;">
                      <span :class="['pct-badge', prod.PaintPercentage === 100 ? 'pct-done' : 'pct-blue']">
                        {{ prod.PaintPercentage }}%
                      </span>
                    </td>
                    <td style="text-align: center;">
                      <span :class="['pct-badge', prod.InstallPercentage === 100 ? 'pct-done' : 'pct-blue']">
                        {{ prod.InstallPercentage }}%
                      </span>
                    </td>
                    <td>
                      <div class="pct-bar-wrapper">
                        <div class="pct-bar-track">
                          <div
                            class="pct-bar-fill"
                            :class="prod.ProcessedPercentage === 100 ? 'pct-bar-done' : 'pct-bar-volume'"
                            :style="{ width: prod.ProcessedPercentage + '%' }"
                          ></div>
                        </div>
                        <span class="pct-bar-label">{{ prod.ProcessedPercentage }}%</span>
                        <span v-if="prod.ProcessedPercentage < 100" class="pct-bar-remaining">
                          {{ 100 - prod.ProcessedPercentage }}% qalıb
                        </span>
                        <span v-else class="pct-bar-complete">✓ Tamamdır</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center py-4 text-muted border rounded bg-white mb-4">
              <i class="fas fa-inbox me-2"></i> Məhsul növü məlumatı yoxdur
            </div>
          </div>
          
          <!-- Close Button centered matching web app -->
          <div class="d-flex justify-content-center mt-4 mb-3">
            <button
              class="btn btn-secondary btn-close-fixed btn-style-gradient-secondary btn-sm task-btn-base"
              style="width: 120px;"
              @click="closeModal"
            >
              Bağla
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { reportApi } from '../services/api'
import Navbar from '../examples/PageLayout/Navbar.vue'

export default {
  name: 'ReportView',
  components: { Navbar },
  data() {
    return {
      token: '',
      client: null,
      sessions: [],
      loading: true,
      error: null,
      searchQuery: '',
      selectedSession: null,
      sessionLocations: [],
      sessionProducts: [],
      modalLoading: false
    }
  },
  computed: {
    filteredSessions() {
      if (!this.searchQuery) return this.sessions
      const q = this.searchQuery.toLowerCase()
      return this.sessions.filter(s => s.SessionName?.toLowerCase().includes(q))
    },
    totalVolume() {
      return this.sessions.reduce((sum, s) => sum + (s.TotalVolume || 0), 0)
    },
    totalWeldPct() {
      if (!this.sessions.length) return 0
      const avg = this.sessions.reduce((sum, s) => sum + (s.WeldPercentage || 0), 0) / this.sessions.length
      return Math.round(avg)
    },
    totalMalyarPct() {
      if (!this.sessions.length) return 0
      const avg = this.sessions.reduce((sum, s) => sum + (s.PaintPercentage || 0), 0) / this.sessions.length
      return Math.round(avg)
    },
    totalInstallPct() {
      if (!this.sessions.length) return 0
      const avg = this.sessions.reduce((sum, s) => sum + (s.InstallPercentage || 0), 0) / this.sessions.length
      return Math.round(avg)
    }
  },
  mounted() {
    // Token-i localStorage-dan oxu (SessionView tərəfindən yazılır)
    const savedToken = localStorage.getItem('report_token')
    if (!savedToken) {
      // Token yoxdur — sessiya səhifəsinə yönləndir
      this.$router.replace('/session/invalid')
      return
    }
    this.token = savedToken
    this.loadReport()
  },
  methods: {
    formatVolume(val) {
      if (!val && val !== 0) return '-'
      return Number(val).toLocaleString('az-AZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    getPctClass(pct) {
      if (pct >= 80) return 'text-success'
      if (pct >= 50) return 'text-warning'
      return 'text-danger'
    },
    getPctColor(pct) {
      if (pct >= 80) return '#198754'
      if (pct >= 50) return '#ffc107'
      return '#dc3545'
    },
    printReport() {
      window.print()
    },
    async loadReport() {
      try {
        this.loading = true
        this.error = null
        const [clientRes, sessionsRes] = await Promise.all([
          reportApi.getClientByToken(this.token),
          reportApi.getClientSessions(this.token)
        ])
        if (clientRes.data?.IsSuccess) {
          this.client = clientRes.data.Data
        } else {
          throw new Error(clientRes.data?.Error || 'Müştəri tapılmadı')
        }
        if (sessionsRes.data?.IsSuccess) {
          this.sessions = sessionsRes.data.Data || []
        } else {
          this.sessions = []
        }
        this.loading = false
      } catch (err) {
        console.error(err)
        this.loading = false
        if (err.response?.status === 404 || err.response?.status === 401) {
          this.error = 'İstifadəçi tapılmadı'
        } else if (err.message) {
          this.error = err.message
        } else {
          this.error = 'Server/şəbəkə xətası'
        }
      }
    },
    async openSessionModal(session) {
      this.selectedSession = session
      this.sessionLocations = []
      this.sessionProducts = []
      this.modalLoading = true
      try {
        const [locRes, prodRes] = await Promise.all([
          reportApi.getSessionLocations(session.SessionId),
          reportApi.getSessionProducts(session.SessionId)
        ])
        if (locRes.data?.IsSuccess) this.sessionLocations = locRes.data.Data || []
        if (prodRes.data?.IsSuccess) this.sessionProducts = prodRes.data.Data || []
      } catch (err) {
        console.error('Modal data error:', err)
      } finally {
        this.modalLoading = false
      }
    },
    closeModal() {
      this.selectedSession = null
      this.sessionLocations = []
      this.sessionProducts = []
    }
  }
}
</script>

<style scoped>
/* Layout */
.report-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

.report-main {
  flex: 1;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 95px 16px 24px 16px; /* Compensate for fixed navbar height */
}

@media (min-width: 769px) {
  .report-main {
    max-width: 1350px !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin: 0 auto !important;
  }
}

/* States */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
  text-align: center;
}
.loading-spinner-icon {
  font-size: 48px;
  color: #6a11cb;
}
.error-icon {
  width: 80px;
  height: 80px;
  background: #fde8e8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #dc3545;
}
.error-state h2 { font-size: 22px; color: #344767; }
.error-state p { color: #6c757d; max-width: 400px; }

/* Report Content */
.report-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Section Header */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  gap: 12px;
  flex-wrap: wrap;
}

/* Stat Cards Grid-5 Layout */
.grid-5 {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 8px;
}

@media (max-width: 1200px) {
  .grid-5 { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 768px) {
  .grid-5 { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 480px) {
  .grid-5 { grid-template-columns: 1fr; }
}

.stat-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  cursor: default;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
}

/* Accents */
.card-purple { border-top: 4px solid #6a11cb; }
.card-violet { border-top: 4px solid #6f42c1; }
.card-green  { border-top: 4px solid #198754; }
.card-orange { border-top: 4px solid #fb6340; }
.card-blue   { border-top: 4px solid #0d6efd; }

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #344767;
  line-height: 1.2;
  margin-top: 4px;
}

.stat-label {
  font-size: 11px;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stat-icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.bg-purple-light { background: rgba(106, 17, 203, 0.1); color: #6a11cb; }
.bg-violet-light { background: rgba(111, 66, 193, 0.1); color: #6f42c1; }
.bg-green-light  { background: rgba(40, 167, 69, 0.1); color: #198754; }
.bg-orange-light { background: rgba(251, 99, 64, 0.1); color: #fb6340; }
.bg-blue-light   { background: rgba(13, 110, 253, 0.1); color: #0d6efd; }

/* ===================================================
   MODAL
   =================================================== */

.report-layout .modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  background-color: rgba(0,0,0,0.5) !important;
  z-index: 11000 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
  transform: none !important;
  display: block !important;
}

.modal-content-wide {
  background: #f8f9fa;
  border-radius: 0;
  width: 100%;
  max-width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: none;
  border: none;
  overflow-x: hidden;
  box-sizing: border-box;
}

@media (min-width: 769px) {
  .report-layout .modal-overlay {
    top: 70px !important;
    left: 50% !important;
    right: auto !important;
    bottom: 0 !important;
    width: 100% !important;
    max-width: 1350px !important;
    height: calc(100vh - 70px) !important;
    transform: translateX(-50%) !important;
    margin: 0 !important;
    border-radius: 8px 8px 0 0;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
    background-color: #f8f9fa !important;
    z-index: 11000 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }

  .modal-content-wide {
    width: 100%;
    max-width: 100%;
    min-height: 100%;
    overflow-x: hidden;
  }
}

/* HEADER GRADIENT */
.report-layout .modal-header-gradient {
  background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);
  padding: 10px 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #fff;
  border-radius: 0 0 15px 15px;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
}

@media (min-width: 769px) {
  .report-layout .modal-header-gradient {
    border-radius: 8px 8px 0 0;
    padding: 16px 24px;
  }
}

/* BODY */
.modal-body-scrollable {
  padding: 16px 15px 20px 15px;
  flex: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: clip;
}

@media (min-width: 769px) {
  .modal-body-scrollable {
    padding: 24px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: clip;
  }
}

.modal-footer-sticky {
  padding: 12px 15px;
  background: #fff;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
}

.modal-header-gradient .back-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  font-size: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
}

.modal-header-gradient .back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.modal-header-gradient .header-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.modal-header-gradient .modal-title-text {
  margin: 0;
  font-weight: 700;
  font-size: 1.25rem;
  color: #fff;
  letter-spacing: -0.02em;
}

.modal-header-gradient .modal-subtitle-text {
  margin: 3px 0 0 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
}
/* Card custom styles */
.modal-info-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}

.modal-info-card .card-title {
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8392ab;
  font-weight: 700;
}

/* Search input */
.search-input {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 13px;
  outline: none;
  width: 220px;
  color: #344767;
  background: #fff;
  transition: border-color 0.2s;
}
.search-input:focus {
  border-color: #6a11cb;
  box-shadow: 0 0 0 0.15rem rgba(106, 17, 203, 0.15);
}

/* Table container */
.table-wrap {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Table styles */
.report-table {
  width: 100%;
  border-collapse: collapse;
}
.report-table th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: bold;
  font-size: 13px;
  padding: 12px 10px;
  border-bottom: 1px solid #ebeef5;
}
.report-table td {
  padding: 12px 10px;
  color: #606266;
  font-size: 13.5px;
  border-bottom: 1px solid #ebeef5;
  vertical-align: middle;
}
.report-table tbody tr:last-child td {
  border-bottom: none;
}
.report-table tbody tr:hover {
  background-color: #f5f7fa;
}

/* Badge-lər */
.pct-badge {
  display: inline-block;
  min-width: 52px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  color: #fff;
}
.pct-grey  { background-color: #6c757d; }
.pct-blue  { background-color: #0d6efd; }
.pct-done  { background-color: #198754; }

/* Progress bar */
.pct-bar-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.pct-bar-track {
  flex: 1;
  height: 14px;
  background: #e9ecef;
  border-radius: 7px;
  overflow: hidden;
  min-width: 80px;
  max-width: 180px;
}
.pct-bar-fill {
  height: 100%;
  border-radius: 7px;
  transition: width 0.4s ease;
}
.pct-bar-progress { background: linear-gradient(90deg, #0d6efd, #3b8ef3); }
.pct-bar-volume   { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.pct-bar-done     { background: linear-gradient(90deg, #198754, #28c97e); }

.pct-bar-label {
  font-size: 12px;
  font-weight: 700;
  color: #333;
  white-space: nowrap;
  min-width: 32px;
}
.pct-bar-remaining {
  font-size: 11px;
  color: #aaa;
  white-space: nowrap;
}
.pct-bar-complete {
  font-size: 11px;
  font-weight: 700;
  color: #198754;
  white-space: nowrap;
}

/* Legend */
.legend-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
}
.legend-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #344767;
  white-space: nowrap;
}
.legend-items {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6c757d;
}
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

@media print {
  .btn, .btn-close, .filters-section, .modal-footer, .search-input, .legend-card {
    display: none !important;
  }
}
</style>
